/**
 * The wall clock the drafter reads, in hearth's `<current_datetime>` shape.
 *
 * It rides the composer's hidden `<workspace_context>` block rather than the
 * system prompt: the system text sits ahead of the whole transcript, so a
 * per-turn timestamp there would re-process the cacheable prefix every turn.
 * On the message it is also durable, so a recovery retry rebuilds the same
 * prompt, and each send carries the time it was actually sent.
 *
 * The time is the browser's own, since that is the member's. `now` is injected
 * so the block is deterministically testable.
 */
export function buildDatetimeContext(now: Date): string {
  return [
    "<current_datetime>",
    `- current_time: ${compactLocal(now)} (${isoZ(now)})`,
    "Assume the member's timezone in conversation unless they say otherwise.",
    "</current_datetime>",
  ].join("\n");
}

/** UTC ISO-8601 to whole-second precision — `2026-07-14T19:04:05Z`. */
function isoZ(d: Date): string {
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * Compact, timezone-aware wall clock — `Tue, Jul 14, 2026, 15:04:05 EDT`. `h23`
 * gives 24-hour time (compact, and no AM/PM narrow no-break space to worry
 * about); the zone comes from the runtime, so it names where the member is.
 */
function compactLocal(now: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  }).format(now);
}
